import { type FC, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { trackEvent } from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import { usePdpSharedTracking } from '../../hooks/usePdpSharedTracking';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { useRespUnavailableContext } from '../PdpRespUnavailableContext/PdpRespUnavailableContext';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { getQueryParam } from '../../../utils/uriUtils';
import scrollIntoView from 'scroll-into-view';
import { BUYER_INCENTIVES } from '../../helpers/tabConstants';
import {
    PDP_SOLD_TOP_BILLBOARD_PLACEMENT,
    PDP_SOLD_BOTTOM_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants';
import { trackBannerAdsAbTestVariant } from 'dibs-google-ad-manager/exports/abTestHelpers';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { PdpRespSharedTitle } from '../../respShared/PdpRespSharedTitle/PdpRespSharedTitle';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import { SharedRespLinksBlock } from '../../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import {
    RecentlyViewedItemsProvider,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { PdpSharedAuctionEndedLink } from '../../shared/PdpSharedAuctionEndedLink/PdpSharedAuctionEndedLink';
import { PdpRespSharedBreadcrumbs } from '../../respShared/PdpRespSharedBreadcrumbs/PdpRespSharedBreadcrumbs';
import { youMayAlsoLike } from '../../../shared/sharedMessages';
import {
    PdpRespUnavailableDetailsTabs,
    useUnavailableDetailTabsState,
} from '../PdpRespUnavailableDetailsTabs/PdpRespUnavailableDetailsTabs';
import { PdpSharedEditorialArticles } from '../../shared/PdpSharedEditorialArticles/PdpSharedEditorialArticles';
import { PdpRespSharedHeroCarousel } from '../../respShared/PdpRespSharedHeroCarousel/PdpRespSharedHeroCarousel';
import { PdpRespSharedSellerChatLoader } from '../../respShared/PdpRespSharedSellerChatLoader/PdpRespSharedSellerChatLoader';
import { PdpRespSharedPrintDetailsLazy } from '../../respShared/PdpRespSharedPrintDetails/PdpRespSharedPrintDetailsLazy';
import { PdpRespSharedQrCodeLazy } from '../../respShared/PdpRespSharedQrCode/PdpRespSharedQrCodeLazy';
import { BrowserHistoryContextProvider } from '../../../global/BrowserHistoryContext/BrowserHistoryContext';
import { PdpSharedSellerItemsCarousel } from '../../shared/PdpSharedSellerItemsCarousel/PdpSharedSellerItemsCarousel';
import { PdpRespRefetchableSimilarItemsGrid } from '../../PdpRespSimilarItems/PdpRespRefetchableSimilarItemsGrid';
import { SharedBillboardAd } from '../../../shared/SharedBillboardAd/SharedBillboardAd';
import { PdpSharedFeaturedRelatedItemsCarouselLazy } from '../../PdpFeaturedRelatedItemsCarousel/PdpSharedFeaturedRelatedItemsCarouselLazy';
import { PdpRespFeaturedItemsGridLazy } from '../../respShared/PdpRespFeaturedItemsGrid/PdpRespFeaturedItemsGridLazy';

// styles
import styles from './PdpRespUnavailableContainer.scss';
import dibsCss from 'dibs-css';

// types
import { type PdpRespUnavailableContainer_item$key as Item } from './__generated__/PdpRespUnavailableContainer_item.graphql';
import { type PdpRespUnavailableContainer_user$key as User } from './__generated__/PdpRespUnavailableContainer_user.graphql';
import { type PdpRespUnavailableContainer_viewer$key as Viewer } from './__generated__/PdpRespUnavailableContainer_viewer.graphql';

// lazy components
const PdpSharedItemFavorites = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../shared/PdpSharedItemFavorites/PdpSharedItemFavorites'
        )
);

const PdpRespSharedHeroCarouselPrint = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../respShared/PdpRespSharedHeroCarouselPrint/PdpRespSharedHeroCarouselPrint'
        )
);

const PdpSharedOptInPromptModal = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespUnavailableContainerLazy" */ '../../PdpSharedOptInPromptModal/PdpSharedOptInPromptModal'
        )
);

const PdpRespSharedSidebarSellerInfoAndContact = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespSharedSidebarSellerInfoAndContact" */ '../../PdpRespSidebarSellerInfo/PdpRespSharedSidebarSellerInfoAndContact'
        )
);

const PdpSharedUnavailableViewPrice = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedUnavailableViewPrice" */ '../../PdpSharedUnavailableViewPrice/PdpSharedUnavailableViewPrice'
        )
);

const ViewPriceToast = dibsLazyClient(
    () =>
        import(/* webpackChunkName: "ViewPriceToast" */ 'dibs-buyer-layout/exports/ViewPriceToast')
);

const viewerFragment = graphql`
    fragment PdpRespUnavailableContainer_viewer on Viewer {
        isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
        ...usePdpSharedTracking_viewer
        ...SharedRespLinksBlock_viewer
        ...PdpRespUnavailableDetailsTabs_viewer
        ...PdpRespSharedHeroCarousel_viewer
        ...PdpRespSharedSellerChatLoader_viewer @include(if: $fetchChat)
        ...PdpSharedSellerItemsCarousel_viewer
        ...PdpRespRefetchableSimilarItemsGrid_viewer @arguments(itemId: $itemId)
        ...PdpSharedFeaturedRelatedItemsCarouselLazy_viewer @include(if: $afterDisplayAndBtNone)
        ...PdpRespFeaturedItemsGridLazy_viewer @include(if: $afterDisplayAndBtNone)
    }
`;
const itemFragment = graphql`
    fragment PdpRespUnavailableContainer_item on Item {
        serviceId
        isAuctionEnabled
        seller {
            serviceId
            isAccessible
            ...PdpRespSharedSidebarSellerInfoAndContact_seller @include(if: $afterDisplay)
        }
        internalBuyLinks {
            ...SharedRespLinksBlock_links
        }
        storyTellingModule(shortTitles: true) {
            tabs {
                supplementaryEditorial {
                    articles {
                        __typename
                    }
                }
            }
        }
        ...PdpRespSharedTitle_item
        ...usePdpSharedTracking_item
        ...SharedRespLinksBlock_item
        ...PdpRespSharedBreadcrumbs_item
        ...PdpRespUnavailableDetailsTabs_item
        ...PdpRespSharedHeroCarousel_item
        ...PdpSharedEditorialArticles_refactor_item
        ...PdpRespSharedPrintDetailsLazy_item
        ...PdpSharedAuctionClosed_item @include(if: $afterDisplay)
        ...PriceDisplay_item
            @include(if: $afterDisplayAndBtSsr)
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
                catchNonFatalErrors: false
            )
        ...PdpSharedItemFavorites_item @include(if: $afterDisplay)
        ...PdpRespSharedHeroCarouselPrint_item @include(if: $afterDisplay)
        ...PdpRespSharedSidebarSellerInfoAndContact_item @include(if: $afterDisplay)
        ...PdpSharedSellerItemsCarousel_item
        ...PdpSharedUnavailableViewPrice_item
        ...PdpRespRefetchableSimilarItemsGrid_item
        ...PdpRespFeaturedItemsGridLazy_item @include(if: $afterDisplayAndBtNone)
    }
`;
const userFragment = graphql`
    fragment PdpRespUnavailableContainer_user on User {
        serviceId
        ...PdpRespUnavailableDetailsTabs_user
        ...PdpRespSharedHeroCarousel_user
        ...PdpRespSharedPrintDetailsLazy_user
        ...PdpSharedItemFavorites_user @include(if: $afterDisplay)
        ...PdpRespSharedSidebarSellerInfoAndContact_user @include(if: $afterDisplay)
        ...PdpSharedSellerItemsCarousel_user
        ...PdpRespRefetchableSimilarItemsGrid_user
        ...PdpSharedFeaturedRelatedItemsCarouselLazy_user @include(if: $afterDisplayAndBtNone)
        ...PdpRespFeaturedItemsGridLazy_user @include(if: $afterDisplayAndBtNone)
    }
`;

export const PdpRespUnavailableContainer: FC<{
    item: Item;
    viewer: Viewer;
    user: User | null;
}> = ({ item: itemRef, user: userRef, viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const { isSsrOnlyBot, isBannerAdsAbTestVariant, isTablet, isItemFavoritingVariant } =
        useServerVarsContext();

    const {
        currency,
        relayVars: { afterDisplay, fetchChat },
    } = useRespUnavailableContext();
    const [showOptinPrompt, setShowOptinPrompt] = useState(false);
    const [viewPriceToast, setViewPriceToast] = useState(false);
    const detailTabsState = useUnavailableDetailTabsState();

    const hasEditorialArticles =
        !!item?.storyTellingModule?.tabs?.[0]?.supplementaryEditorial?.articles?.length;

    usePdpSharedTracking({ item, viewer });

    const { serviceId: userId } = user || {};
    const { isWordpressArticlesEnabled } = viewer;
    const { seller, serviceId: itemId, internalBuyLinks, isAuctionEnabled } = item;

    useDwelledOnPage(PAGE_TYPE.PDP);

    const similarItemsRef = useRef(null);

    const scrollToBuyerIncentives = useRef(true);
    const detailsSectionRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const displayBuyerIncentivesParam = getQueryParam(
            location.search,
            'displayBuyerIncentives'
        );
        const shouldScrollToBuyerIncentivesDetails =
            scrollToBuyerIncentives.current && displayBuyerIncentivesParam === 'true';
        if (shouldScrollToBuyerIncentivesDetails) {
            const detailsSection = detailsSectionRef?.current;
            if (detailsSection) {
                const [, setActiveTab] = detailTabsState;
                setActiveTab(BUYER_INCENTIVES);
                scrollToBuyerIncentives.current = false;
                scrollIntoView(detailsSection, { time: 500 }, () => detailsSection.focus());
            }
        }
    }, [detailTabsState]);

    useEffect(() => {
        trackBannerAdsAbTestVariant();
    }, []);

    const priceDisplay = (
        <PriceDisplay
            sku={null}
            useSvgIcon
            item={item}
            displayVatLabel
            currency={currency}
            hasPriceBlur={!userId}
            showSoldListPrice={false}
        />
    );

    return (
        <>
            <div className={styles.printHide}>
                <PdpRespSharedBreadcrumbs item={item} />
            </div>
            <div className="rowFlex" data-tn="pdp-main-container">
                <div
                    className={`${styles.topContainer} ${styles.carouselContainer} colXs12 colLg6 colLgOffset1 colXl6 colXlOffset1`}
                >
                    <div className={styles.printHide}>
                        <BrowserHistoryContextProvider>
                            <PdpRespSharedHeroCarousel
                                viewer={viewer}
                                item={item}
                                user={user}
                                currency={currency}
                                afterDisplay={afterDisplay}
                                size="xxSmall"
                            />
                        </BrowserHistoryContextProvider>
                        {isItemFavoritingVariant && (
                            <div
                                className={classnames(
                                    styles.printHide,
                                    styles.favoritesContainer,
                                    dibsCss.absolute,
                                    dibsCss.topMedium,
                                    dibsCss.rightXxlarge,
                                    dibsCss.prSmaller
                                )}
                            >
                                <ClientSuspense fallback={null}>
                                    <PdpSharedItemFavorites
                                        item={item}
                                        user={user}
                                        showHeartCount
                                        wrapperClass={styles.favoritesWrapper}
                                        heartIconWrapperClass={styles.heartIconWrapper}
                                        animateHeartPulse={false}
                                        showNudge
                                        theme="circle-frosted-medium"
                                    />
                                </ClientSuspense>
                            </div>
                        )}
                    </div>
                    <ClientSuspense fallback={null}>
                        <div className={styles.printShow}>
                            <PdpRespSharedHeroCarouselPrint item={item} />
                        </div>
                    </ClientSuspense>
                </div>
                <div className={`${styles.topContainer} colXs12 colLg4 colXl4`}>
                    <div className={styles.sidebarContainer}>
                        {isAuctionEnabled && <PdpSharedAuctionEndedLink />}
                        <div className="rowFlex">
                            <div
                                className={classnames('colXs12', {
                                    ['colLg10']: !isItemFavoritingVariant,
                                })}
                            >
                                <PdpRespSharedTitle item={item} isUnavailablePdp />
                                <div className={styles.printHide}>
                                    {isSsrOnlyBot ? (
                                        priceDisplay
                                    ) : (
                                        <ClientSuspense fallback={null}>
                                            {priceDisplay}
                                        </ClientSuspense>
                                    )}
                                </div>
                            </div>
                            {!isItemFavoritingVariant && (
                                <div
                                    className={`${styles.printHide} ${styles.favoritesContainer} colXs2`}
                                >
                                    <ClientSuspense fallback={null}>
                                        <PdpSharedItemFavorites
                                            item={item}
                                            user={user}
                                            showHeartCount
                                            wrapperClass={styles.favoritesWrapper}
                                            heartIconWrapperClass={styles.heartIconWrapper}
                                        />
                                    </ClientSuspense>
                                </div>
                            )}
                        </div>
                        <ClientSuspense fallback="">
                            {!userId && (
                                <PdpSharedUnavailableViewPrice
                                    item={item}
                                    currency={currency}
                                    launchOrigin="sold gating pdp sidebar"
                                    setViewPriceToast={setViewPriceToast}
                                />
                            )}
                        </ClientSuspense>
                        {seller?.isAccessible && (
                            <ClientSuspense fallback={null}>
                                <div
                                    className={classnames(
                                        dibsCss.myXsmall,
                                        styles.topDivider,
                                        styles.printHide
                                    )}
                                >
                                    <PdpRespSharedSidebarSellerInfoAndContact
                                        template="UNAVAILABLE"
                                        user={user}
                                        item={item}
                                        seller={item.seller}
                                        currency={currency}
                                        isClient={afterDisplay}
                                    />
                                </div>
                            </ClientSuspense>
                        )}
                        <PdpRespSharedQrCodeLazy itemId={itemId} className={styles.printShow} />
                    </div>
                </div>
            </div>

            {isBannerAdsAbTestVariant && (
                <SharedBillboardAd
                    placement={PDP_SOLD_TOP_BILLBOARD_PLACEMENT}
                    className={dibsCss.myLarge}
                />
            )}

            <div ref={detailsSectionRef} className={styles.printHide}>
                <PdpRespUnavailableDetailsTabs
                    user={user}
                    item={item}
                    viewer={viewer}
                    tabsState={detailTabsState}
                />
            </div>
            <div className={styles.printShow}>
                <PdpRespSharedPrintDetailsLazy item={item} user={user} currency={currency} />
            </div>

            {fetchChat && <PdpRespSharedSellerChatLoader viewer={viewer} currency={currency} />}

            <div className={styles.printHide}>
                <div ref={similarItemsRef}>
                    <PdpRespRefetchableSimilarItemsGrid
                        viewer={viewer}
                        user={user}
                        item={item}
                        header={youMayAlsoLike}
                        relayVariables={{ isClient: afterDisplay }}
                        currency={currency}
                        favoritesLocation="SIMILAR_ITEM_GRID"
                        gridLocation="SIMILAR_ITEM_GRID"
                        dataTn="pdp-similar-recommendation-grid"
                        isDark={false}
                        showLightBox={false}
                        hideBar
                        gridItemWrapperClass={styles.similarItem}
                    />
                </div>
                <PdpSharedSellerItemsCarousel
                    viewer={viewer}
                    item={item}
                    user={user}
                    currency={currency}
                />

                {/* Featured Related Items */}
                <PdpSharedFeaturedRelatedItemsCarouselLazy
                    viewer={viewer}
                    user={user}
                    afterDisplay={afterDisplay}
                    currency={currency}
                />

                {isBannerAdsAbTestVariant && (
                    <SharedBillboardAd
                        placement={PDP_SOLD_BOTTOM_BILLBOARD_PLACEMENT}
                        className={dibsCss.myLarge}
                    />
                )}

                <RecentlyViewedItemsProvider
                    excludedItemIds={item?.serviceId ? [item?.serviceId] : null}
                    deviceType={isTablet ? TABLET : DESKTOP}
                >
                    <RecentlyViewedItemsLazy
                        currency={currency}
                        authModalLoader={authModalLoader}
                        hasTopSeparator
                        hasBottomSeparator={
                            hasEditorialArticles && !!viewer.isWordpressArticlesEnabled
                        }
                    />
                </RecentlyViewedItemsProvider>

                <PdpRespFeaturedItemsGridLazy
                    isUnavailablePdp
                    viewer={viewer}
                    user={user}
                    item={item}
                    afterDisplay={afterDisplay}
                    currency={currency}
                />
            </div>

            {isWordpressArticlesEnabled && (
                <div className={styles.printHide}>
                    <PdpSharedEditorialArticles item={item} />
                </div>
            )}

            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    item={item}
                    viewer={viewer}
                    itemSearch={null}
                    onClick={() => {
                        trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                    links={internalBuyLinks?.filter(filterNulls) || null}
                />
            </div>

            {showOptinPrompt && (
                <ClientSuspense fallback="">
                    <PdpSharedOptInPromptModal
                        isAuctionEnabled={false}
                        onModalClose={() => setShowOptinPrompt(false)}
                    />
                </ClientSuspense>
            )}

            {viewPriceToast && (
                <ClientSuspense fallback="">
                    <ViewPriceToast
                        isOpen={viewPriceToast}
                        onClose={() => setViewPriceToast(false)}
                    />
                </ClientSuspense>
            )}
        </>
    );
};
